/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as R from 'ramda';

import { SmartForm } from 'components';
import { saveSetting, getSetting } from 'rdx/config';
import { valueToInteger } from 'utils';
import {
  getBulkConfigurations,
  saveConfiguration,
  updateConfiguration,
} from 'service/ConfigAPI';
import { getCurrentUserHasRightToChangeVersion } from 'rdx/users';
import { addError } from 'rdx/alert';

import settingsSchema from './settingsSchema';

import './generalSettings.scss';

function GeneralSettings() {
  const dispatch = useDispatch();
  const settingsFormRef = React.useRef(null);

  /** *** Start of Settings Selectors ***** */

  const touchpos_use_dark_theme = useSelector(
    getSetting('touchpos_use_dark_theme'),
  );
  const touchpos_hide_employee_stats = useSelector(
    getSetting('touchpos_hide_employee_stats'),
  );
  const uiPreviousConfig = useSelector(getSetting('touchpos_ui_configuration'));
  const touchpos_disable_unused_coupons_popup = useSelector(
    getSetting('touchpos_disable_unused_coupons_popup'),
  );
  const pos_timeout = useSelector(getSetting('pos_timeout'));
  const touchpos_orders_only_from_current_location = useSelector(
    getSetting('touchpos_orders_only_from_current_location'),
  );
  const touchpos_laybys_only_from_current_location = useSelector(
    getSetting('touchpos_laybys_only_from_current_location'),
  );
  const touchpos_recent_sales_only_from_current_location = useSelector(
    getSetting('touchpos_recent_sales_only_from_current_location'),
  );
  const touchpos_unfinished_sales_only_from_current_location = useSelector(
    getSetting('touchpos_unfinished_sales_only_from_current_location'),
  );
  const touchpos_order_to_invoice_in_current_warehouse = useSelector(
    getSetting('touchpos_order_to_invoice_in_current_warehouse'),
  );
  const touchpos_disable_selling_to_default_customer = useSelector(
    getSetting('touchpos_disable_selling_to_default_customer'),
  );
  const touchscreen_pos = useSelector(getSetting('touchscreen_pos'));

  /** *** End of Settings Selectors ***** */

  const canEditVersion = useSelector(getCurrentUserHasRightToChangeVersion);

  const [prevSettings, setPrevSettings] = useState({});
  const [uiSettingsState, setUISettings] = useState({});
  const [versionState, setVersionState] = useState({});
  const [versionConfExists, setVersionConfExists] = useState(false);
  const [versionConfFetched, setVersionConfFetched] = useState(false);

  useEffect(() => {
    if (uiPreviousConfig) {
      const settingsObject = JSON.parse(uiPreviousConfig);
      setUISettings({ ...settingsObject });
    }
  }, [uiPreviousConfig]);

  // init fetch the version config
  useEffect(() => {
    getBulkConfigurations({
      level: 'Company',
      level_id: '',
      type: 'global',
      name: 'posBrazilVersion',
    })
      .then((resp) => {
        const { value } = resp?.data?.[0] ?? {};
        if (value) {
          setVersionConfExists(true);
          setVersionState(value);
        }
      })
      .catch(() => {
        dispatch(addError('Unable to fetch existing version control configs!'));
      })
      .finally(() => {
        setVersionConfFetched(true);
      });
  }, [dispatch]);

  useEffect(() => {
    setPrevSettings({
      touchpos_use_dark_theme,
      touchpos_hide_employee_stats,
      touchpos_disable_unused_coupons_popup,
      pos_timeout,
      touchpos_orders_only_from_current_location,
      touchpos_laybys_only_from_current_location,
      touchpos_recent_sales_only_from_current_location,
      touchpos_unfinished_sales_only_from_current_location,
      touchpos_order_to_invoice_in_current_warehouse,
      touchpos_disable_selling_to_default_customer,
      touchscreen_pos,
      ...versionState,
      ...uiSettingsState,
    });
  }, [
    touchpos_use_dark_theme,
    touchpos_hide_employee_stats,
    touchpos_disable_unused_coupons_popup,
    pos_timeout,
    touchpos_orders_only_from_current_location,
    touchpos_laybys_only_from_current_location,
    touchpos_recent_sales_only_from_current_location,
    touchpos_unfinished_sales_only_from_current_location,
    touchpos_order_to_invoice_in_current_warehouse,
    touchpos_disable_selling_to_default_customer,
    touchscreen_pos,
    uiSettingsState,
    versionState,
  ]);

  const submitSetting = () => {
    const { values } = settingsFormRef.current;

    const prevUIConfig = uiPreviousConfig ? JSON.parse(uiPreviousConfig) : {};
    const newUISettings = { ...prevUIConfig };

    Object.entries(values).forEach(([key, value]) => {
      const payload = {};

      if (key === 'hideCustomerSearch' || key === 'hideCustomerInfo') {
        newUISettings[key] = valueToInteger(value);

        if (newUISettings[key] !== prevSettings[key]) {
          dispatch(
            saveSetting({
              parameterName: 'touchpos_ui_configuration',
              parameterValue: JSON.stringify(newUISettings),
            }),
          );
        }
      } else if (key === 'pos_timeout') {
        payload.parameterName = key;
        payload.parameterValue = value;

        if (value !== prevSettings[key]) {
          dispatch(saveSetting(payload));
        }
      } else if (['develop', 'production', 'sandbox'].includes(key)) {
        const prevConf = {
          sandbox: prevSettings.sandbox,
          develop: prevSettings.develop,
          production: prevSettings.production,
        };
        if (prevSettings[key] !== value) {
          const body = {
            level: 'Company',
            level_id: '',
            type: 'global',
            name: 'posBrazilVersion',
            value: {
              ...prevConf,
              [key]: value,
            },
          };
          if (versionConfExists) {
            updateConfiguration(body);
          } else {
            saveConfiguration(body);
            setVersionConfExists(true);
          }
          setVersionState(body.value);
        }
      } else {
        payload.parameterName = key;
        payload.parameterValue = valueToInteger(value);

        if (payload.parameterValue !== prevSettings[key]) {
          dispatch(saveSetting(payload));
        }
      }
    });
  };

  const editedSettingsSchema = useMemo(
    () =>
      settingsSchema.map((setting) => {
        if (['develop', 'production', 'sandbox'].includes(setting.name)) {
          return R.pipe(
            R.assocPath(
              ['input', 'disabled'],
              !canEditVersion || !versionConfFetched,
            ),
            R.assocPath(
              ['input', 'options'],
              !versionConfFetched ? ['Loading...'] : setting.input.options,
            ),
          )(setting);
        }
        return setting;
      }),
    [canEditVersion, versionConfFetched],
  );

  return (
    <SmartForm
      grid
      autosave
      ref={settingsFormRef}
      fields={editedSettingsSchema}
      state={prevSettings}
      handleSubmit={submitSetting}
    />
  );
}

export default GeneralSettings;
